<template>
  <v-container fluid>
    <el-overlay-loading :loading="pageloading" />

    <h3 class="mb-5">Dokumento informacija</h3>

    <div class="dataContainer">
      <v-row dense>
        <v-col cols="12" md="6">
          <el-autocomplete
            :items="skubos"
            itemvalue="value"
            itemtext="text"
            :label="skubaTitle"
            :tooltip="`${ntrTooltipPrefix} Registravimas. ${skubaTitleTooltip}`"
            need
            :readonly="isReadonlyForm"
            v-model="formData.pra_skuba"
            @change="checkData()"
          />
        </v-col>
      </v-row>
    </div>

    <v-row dense>
      <v-col
        class="bold-label mt-2"
        cols="12"
        md="6"
        v-if="showUzsakytiIsrasai"
      >
        <h4 class="mb-2">Užsakyti NTR išrašai</h4>

        <v-data-table
          :headers="filteredTurtasHeaders"
          :items="turtasList"
          :loading="loading"
          :footer-props="{
            showFirstLastPage: true,
            showCurrentPage: true,
            itemsPerPageText: 'Rodyti po:',
            itemsPerPageAllText: 'visi',
            itemsPerPageOptions: itemsPerPageOptions,
          }"
          :hide-default-footer="hideTurtasFooter"
          :items-per-page="pageSize"
        >
          <template #[`item.bylos`]="{ item }">
            <div class="nowrap">
              <el-byla
                v-for="byla in item.bylos"
                :key="byla.byl_id"
                :byla="byla"
              />
            </div>
          </template>
          <template slot="loading">
            Kraunasi...
          </template>
          <template slot="no-results">
            Duomenų nėra
          </template>
          <template slot="no-data">
            Duomenų nėra
          </template>
          <template #[`footer.page-text`]="props">
            Rodoma {{ props.pageStart }}-{{ props.pageStop }} iš
            {{ props.itemsLength }}
          </template>
        </v-data-table>
      </v-col>

      <v-col
        class="bold-label mt-2 pl-5"
        cols="12"
        md="6"
        v-if="showZemesSklypai"
      >
        <h4 class="mb-2">Žemės sklypų planai</h4>

        <v-data-table
          :headers="filteredSklypasHeaders"
          :items="sklypasList"
          :loading="loading"
          :footer-props="{
            showFirstLastPage: true,
            showCurrentPage: true,
            itemsPerPageText: 'Rodyti po:',
            itemsPerPageAllText: 'visi',
            itemsPerPageOptions: itemsPerPageOptions,
          }"
          :hide-default-footer="hideSklypasFooter"
          :items-per-page="pageSize"
        >
          <template #[`item.byl_rusis`]="{ item }">
            <div class="nowrap">
              {{ item.byl_rusis }}
            </div>
            <div class="nowrap text-gray" v-if="item.sklypo_turtas">
              {{ item.sklypo_turtas }}
            </div>
          </template>
          <template #[`item.actions`]="{ item }">
            <div class="nowrap">
              <el-table-action :item="item" />
            </div>
          </template>
          <template slot="loading">
            Kraunasi...
          </template>
          <template slot="no-results">
            Duomenų nėra
          </template>
          <template slot="no-data">
            Duomenų nėra
          </template>
          <template #[`footer.page-text`]="props">
            Rodoma {{ props.pageStart }}-{{ props.pageStop }} iš
            {{ props.itemsLength }}
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <prasymas-informacija
      :refresh-key="`reg-informacija-${registravimasRefresh}`"
      :readonly="isReadonly"
      :readonly-form="isReadonlyForm"
    />

    <h3 class="mt-3 mb-3">Paslaugos užsakymo informacija</h3>

    <reg-prasymas-paslaugos
      :refresh-key="`reg-paslaugos-${registravimasRefresh}`"
      :readonly="isReadonly"
      :is-perduotas="isPerduotas"
      :readonly-form="isReadonlyForm"
    />

    <modal-ntr-busena
      :key="modalBusenaKey"
      v-model="showModalBusena"
      :busena="modalBusena"
      :busena-tekstui="modalBusenaTekstui"
      :is-sign="modalBusenaWithSign"
      @confirmBusena="confirmBusena"
      @loadedBusena="loadedBusena"
    />

    <v-footer class="form-footer" :sticky="true">
      <el-button
        v-if="validate('Perduoti')"
        primary
        @click.native="pasirasymasClick()"
        :loading="loading"
        >Perduoti</el-button
      >
      <el-button
        v-if="validate('Pasirašyti perduotas')"
        primary
        @click.native="openBusena(prasymasBusenaTolimesne)"
        :loading="loading"
        >Pasirašyti</el-button
      >

      <el-button
        v-if="validate('Registruoti')"
        primary
        @click.native="openBusena('iregistruotas')"
        :loading="loading"
        >Registruoti</el-button
      >
      <el-button
        v-if="validate('Pasirašyti vadovas')"
        primary
        @click.native="openBusena(prasymasBusenaTolimesne)"
        :loading="loading"
        >Pasirašyti</el-button
      >
      <el-button
        v-if="validate('Grąžinti tikslinti')"
        @click.native="openBusenaGrazintas('grazintas')"
        :loading="loading"
        >Grąžinti tikslinti</el-button
      >
      <el-button
        v-if="validate('Atmesti')"
        @click.native="openBusena('atmestas')"
        :loading="loading"
        >Atmesti</el-button
      >

      <v-spacer></v-spacer>
      <el-button v-if="showNtrJson" @click.native="ntrJson()" :loading="loading"
        >NTR Json</el-button
      >
      <el-button
        v-if="validate('Atšaukti asmens prašymu')"
        @click.native="openBusena('atsauktas')"
        :loading="loading"
        >Atšaukti asmens prašymu</el-button
      >
      <el-button
        v-if="validate('Priskirti sau')"
        @click.native="priskirtiSau()"
        :loading="loading"
        >Priskirti sau</el-button
      >
      <el-button
        v-if="validate('Word')"
        @click.native="printWord()"
        :loading="loading"
        >Word</el-button
      >
      <el-button
        v-if="!isReadonly"
        primary
        @click.native="checkErrorsClick()"
        :loading="loadingTikrinti"
        >Tikrinti</el-button
      >
    </v-footer>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import checkErrors from '@/mixins/checkErrors'
import RegPrasymasPaslaugos from './RegPrasymasPaslaugos'
import userRole from '@/mixins/userRole'
import busena from '@/mixins/busenaNtr'
import prasymasVeiksmas from '@/mixins/prasymasVeiksmas'
import PrasymasInformacija from './PrasymasInformacija'
import ModalNtrBusena from '../modals/ModalNtrBusena'
import unsavedData from '@/mixins/unsavedData'
import tooltipNtr from '@/mixins/tooltipNtr'

export default {
  name: 'RegPrasymasRegistravimas',
  components: {
    PrasymasInformacija,
    RegPrasymasPaslaugos,
    ModalNtrBusena,
  },
  props: {
    readonlyForm: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
  },
  mixins: [
    userRole,
    prasymasVeiksmas,
    checkErrors,
    busena,
    unsavedData,
    tooltipNtr,
  ],
  $_veeValidate: {
    validator: 'new',
  },
  data() {
    return {
      pageloading: true,
      loading: true,
      formData: {},
      skubos: [],
      turtai: [],
      sklypai: [],
      itemsPerPageOptions: [10, 50, -1],
      pageSize: 10,
      registravimasRefresh: 0,
    }
  },
  computed: {
    ...mapGetters([
      'prasymasID',
      'prasymasTipas',
      'prasymasBusena',
      'prasymasBusenaTolimesne',
      'prasymasReadonly',
      'prasymasSistema',
      'appSavitarna',
    ]),
    isSistemaSavitarna() {
      return this.prasymasSistema === 'savitarna'
    },
    isStepRegistravimas() {
      return true
    },
    showUzsakytiIsrasai() {
      return this.isSistemaSavitarna
    },
    showZemesSklypai() {
      return this.isSistemaSavitarna && this.sklypai.length > 0
    },
    isReadonly() {
      return this.readonly
    },
    isReadonlyForm() {
      return this.readonlyForm
    },
    skubaTitle() {
      if (this.isPrasymasSeima && this.isPrasymasPerkelimas) {
        return this.$t('Juridinio fakto perkėlimo skuba')
      }
      if (this.isPrasymasIsregistravimas || this.isPrasymasNutraukimas) {
        return 'Juridinio fakto išregistravimo skuba'
      }
      if (this.isPrasymasPakeitimas) {
        return 'Juridinio fakto pakeitimo skuba'
      }
      return 'Juridinio fakto įregistravimo skuba'
    },
    skubaTitleTooltip() {
      if (this.isPrasymasSeima && this.isPrasymasPerkelimas) {
        return this.$t('Juridinio fakto pakeitimo skuba')
      }
      if (this.isPrasymasIsregistravimas || this.isPrasymasNutraukimas) {
        return 'Juridinio fakto išregistravimo skuba'
      }
      if (this.isPrasymasPakeitimas) {
        return 'Juridinio fakto pakeitimo skuba'
      }
      return 'Juridinio fakto įregistravimo skuba'
    },
    turtasHeaders() {
      return [
        {
          text: 'Registras',
          align: 'start',
          value: 'tur_registras',
          width: 80,
        },
        {
          text: 'Adresas',
          align: 'start',
          value: 'tur_ntr_adresas',
        },
        {
          text: 'Išrašas',
          align: 'start',
          value: 'bylos',
          width: 50,
          hide: !this.showIsrasasBylos,
        },
      ]
    },
    filteredTurtasHeaders() {
      return this.turtasHeaders.filter(item => {
        return !item.hide
      })
    },
    turtasList() {
      return this.turtai
        .filter(turtas => {
          return this.formData.pra_israsai.includes(turtas.reg_id)
        })
        .map(turtas => {
          turtas.bylos = turtas.bylos.map(byla => {
            return {
              ...byla,
              byl_rusis: byla.byl_tipas,
              byl_tipas: 'ntr_israsas',
              byl_url: `ntrpaslaugos/byla/${byla.byl_id}/${byla.byl_hash}/content`,
            }
          })
          turtas.canDownload = true

          return turtas
        })
    },
    hideTurtasFooter() {
      return this.turtasList.length <= 10
    },
    showIsrasasBylos() {
      return this.isIregistruotas
    },
    sklypasHeaders() {
      return [
        {
          text: 'Dokumento tipas',
          align: 'start',
          sortable: false,
          value: 'byl_rusis',
        },
        {
          text: 'Užsakytas RC savitarnoje',
          align: 'center',
          sortable: false,
          value: 'uzsakytasTxt',
          width: 120,
        },
        {
          text: 'Planas',
          align: 'center',
          sortable: false,
          value: 'actions',
          width: 50,
        },
      ]
    },
    filteredSklypasHeaders() {
      return this.sklypasHeaders.filter(item => {
        return !item.hide
      })
    },
    sklypasList() {
      return this.sklypai.map(byla => {
        return {
          ...byla,
          byl_rusis: 'Žemės sklypo planas',
          byl_tipas: 'download',
          byl_url: `ntrpaslaugos/byla/${byla.byl_id}/${byla.byl_hash}/content`,
          uzsakytas: byla.byl_sugeneruota === 6,
          uzsakytasTxt: byla.byl_sugeneruota === 6 ? 'Taip' : 'Ne',
          canDownload: true,
        }
      })
    },
    hideSklypasFooter() {
      return this.sklypasList.length <= 10
    },
    isLocal() {
      return process.env.VUE_APP_LOCAL === 'local'
    },
    isDevelop() {
      return process.env.VUE_APP_LOCAL === 'develop'
    },
    isTest() {
      return process.env.VUE_APP_LOCAL === 'test'
    },
    showNtrJson() {
      return this.isLocal || this.isDevelop || this.isTest
    },
  },
  created() {
    this.getData()
  },
  methods: {
    getData() {
      this.pageloading = true
      this.loading = true
      this.$axios
        .get(`ntrpaslaugos/prasymas/${this.prasymasID}/registravimas`)
        .then(res => {
          this.formData = res.data.registravimas
          this.turtai = res.data.turtai
          this.sklypai = res.data.sklypai
          this.skubos = res.data.skubos
          this.$nextTick(() => {
            this.initialForm()
          })
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
          this.pageloading = false
        })
    },
    saveData(toRoute, afterSave, busena = '') {
      if (toRoute) {
        this.goToRoute()
        return
      }
      if (this.isReadonlyForm && aftersave !== 'openbusena') {
        return
      }
      this.$validator.validateAll().then(res => {
        if (res) {
          this.loading = true
          let data = this.formData
          data.pra_id = this.prasymasID

          this.$axios
            .post(
              `ntrpaslaugos/prasymas/${this.prasymasID}/registravimas`,
              data
            )
            .then(res => {
              this.updateErrors()
              this.initialForm()
              if (afterSave === 'openbusena') {
                this.openBusena(busena)
              }
              this.$root.$emit('reloadPrasymasTab')
              this.registravimasRefresh++
            })
            .catch(err => {
              window._VMA.$emit('DATA_ERROR', err.response.data.message)
            })
            .finally(() => {
              this.loading = false
            })
        } else {
          window._VMA.$emit('FORMAT_ERROR')
          this.$root.$emit('cancelTabNavigation')
        }
      })
    },
    cancelData() {
      this.getData()
    },
    checkErrorsClick() {
      this.loadingTikrinti = true
      this.checkNtrErrors()
    },
    pasirasymasClick() {
      this.saveData(null, 'openbusena', 'perduotas')
    },
    ntrJson() {
      this.$axios
        .get(`ntrpaslaugos/prasymas/${this.prasymasID}/ntrjson`)
        .then(res => {
          let fileData = res.data.json
          var fileURL = window.URL.createObjectURL(new Blob([fileData]))
          var fileLink = document.createElement('a')
          fileLink.href = fileURL
          fileLink.setAttribute('download', this.safeFileName('ntr.json'))
          document.body.appendChild(fileLink)
          fileLink.click()
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
    },
  },
}
</script>
<style lang="scss" scoped>
.dataContainer {
  max-width: 800px;
}
</style>
